import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Theme,
    Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {useEffect, useState} from 'react';
import {DateTime, Interval} from 'luxon';
import {useDispatch, useSelector} from 'react-redux';
import Command from '../../../state/actions/command';
import TagBar from './trackTimeDialog/TagBar';
import {DesktopDatePicker} from '@mui/x-date-pickers/DesktopDatePicker';
import {Selector} from '../../../state/selector';
import {User} from '../../../state/types/User';

const MAX_DAY_DIFFERENCE = 60;
const MAX_MINUTES_DIFFERENCE = 30;

const styles = {
    startEndWrapper: (theme: Theme) => ({
        marginTop: 1,
        display: 'flex',
        flexDirection: 'column',
        [theme.breakpoints.up('md')]: {
            flexDirection: 'row',
            alignItems: 'center',
        },
    }),
    endWrapper: (theme: Theme) => ({
        [theme.breakpoints.down('md')]: {
            marginTop: 1,
        },
        [theme.breakpoints.up('md')]: {
            marginLeft: 1,
        },
    }),
};

interface TrackTimeDialogProps {
    open: boolean;
    onClose: () => void;
    projectId?: string;
    projectEmployees?: string[];
    availableTags?: string[];
}

const TrackTimeDialog = (props: TrackTimeDialogProps) => {
    const dispatch = useDispatch();
    const isAdmin = useSelector(Selector.User.isAdmin);
    const currentUserId = useSelector(Selector.User.userId);
    const users = useSelector(Selector.User.users);
    const [advancedMode, setAdvancedMode] = useState<boolean>(false);
    const [selectedDate, setSelectedDate] = useState<DateTime | null>(DateTime.now());
    const [durationHours, setDurationHours] = useState<number>(1);
    const [selectedUserId, setSelectedUserId] = useState<string>('');
    const [start, setStart] = useState<Date | null>();
    const [end, setEnd] = useState<Date | null>();
    const [tags, setTags] = useState<string[]>([]);
    const [description, setDescription] = useState<string>('');
    const [validTime, setValidTime] = useState<boolean>(true);
    const timeZoneDiff = new Date().getTimezoneOffset();
    const min = DateTime.now().minus({ days: MAX_DAY_DIFFERENCE + 1 }).toJSDate();
    const max = DateTime.now().plus({ minutes: MAX_MINUTES_DIFFERENCE }).toJSDate();

    useEffect(() => {
        if (props.projectEmployees) {
            props.projectEmployees.forEach((employeeId) => {
                if (!users[employeeId]) {
                    dispatch(Command.User.fetchUser({ userId: employeeId }));
                }
            });
        }
    }, [props.projectEmployees]);

    useEffect(() => {
        setAdvancedMode(false);
        setSelectedDate(DateTime.now());
        setDurationHours(1);
        setSelectedUserId(currentUserId);
        setStart(DateTime.now().minus({ hour: 2 }).startOf('hour').toJSDate());
        setEnd(DateTime.now().startOf('hour').toJSDate());
        setTags([]);
        setDescription('');
    }, [props.open]);

    useEffect(() => {
        if (advancedMode) {
            setValidTime(start && end ? start < end && start > min && end < max : false);
        } else {
            const validDuration = durationHours > 0 && durationHours <= 24;
            const validDate = selectedDate !== null
                && selectedDate >= DateTime.now().minus({ days: MAX_DAY_DIFFERENCE })
                && selectedDate <= DateTime.now().endOf('day');
            setValidTime(validDuration && validDate);
        }
    }, [start, end, advancedMode, durationHours, selectedDate]);

    const getEmployeeName = (employeeId: string): string => {
        const user: User | undefined = users[employeeId];
        return user ? `${user.firstName} ${user.lastName}` : employeeId;
    };

    const trackTime = () => {
        const userIdOverride = isAdmin && selectedUserId ? { userId: selectedUserId } : {};

        if (advancedMode) {
            if (!start || !end) {
                return;
            }
            dispatch(Command.WorkingTime.recordWorkingTime({
                projectId: props.projectId!,
                start: start,
                end: end,
                note: description,
                tags,
                ...userIdOverride,
            }));
        } else {
            if (!selectedDate) {
                return;
            }
            const syntheticStart = selectedDate.startOf('day').toJSDate();
            const minutes = Math.round(durationHours * 60);
            const syntheticEnd = selectedDate.startOf('day').plus({ minutes }).toJSDate();
            dispatch(Command.WorkingTime.recordWorkingTime({
                projectId: props.projectId!,
                start: syntheticStart,
                end: syntheticEnd,
                note: description,
                tags,
                durationOnly: true,
                durationMinutes: minutes,
                ...userIdOverride,
            }));
        }
        props.onClose();
    };

    const timeDiffDisplay = () => {
        if (advancedMode) {
            return validTime && start && end
                ? Interval.fromDateTimes(start, end).length('hours').toFixed(2) + ' Stunden'
                : <span style={{color: 'red'}}>Ungültige Zeitspanne!</span>;
        }
        return durationHours > 0
            ? durationHours.toFixed(2) + ' Stunden'
            : <span style={{color: 'red'}}>Ungültige Dauer!</span>;
    };

    const defaultDateString = (date: Date | null) => {
        if (!date) {
            return '';
        }
        const diffDate = new Date(date);
        diffDate.setMinutes(diffDate.getMinutes() - timeZoneDiff);
        return (diffDate.toISOString().slice(0, 16));
    };

    return (
        <Dialog open={props.open} fullWidth={true} maxWidth={'md'}>
            <DialogTitle>Zeit erfassen</DialogTitle>
            <DialogContent>
                {isAdmin && props.projectEmployees && props.projectEmployees.length > 0 && (
                    <Box sx={{ marginBottom: 2, marginTop: 1 }}>
                        <FormControl sx={{ minWidth: 250 }}>
                            <InputLabel id={'employee-select-label'}>Mitarbeiter</InputLabel>
                            <Select
                                labelId={'employee-select-label'}
                                value={selectedUserId}
                                onChange={(e) => setSelectedUserId(e.target.value as string)}
                                label={'Mitarbeiter'}
                            >
                                {props.projectEmployees.map((employeeId) => (
                                    <MenuItem key={employeeId} value={employeeId}>
                                        {getEmployeeName(employeeId)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                )}
                <Box sx={{ marginBottom: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={advancedMode}
                                onChange={(e) => setAdvancedMode(e.target.checked)}
                            />
                        }
                        label={'Erweitert (Start/Ende)'}
                    />
                </Box>
                {!advancedMode && (
                    <Box sx={styles.startEndWrapper}>
                        <Box>
                            <DesktopDatePicker
                                label={'Datum'}
                                value={selectedDate}
                                onChange={setSelectedDate}
                                renderInput={(params) => <TextField {...params} />}
                                maxDate={DateTime.now()}
                                minDate={DateTime.now().minus({ days: MAX_DAY_DIFFERENCE })}
                                inputFormat={'dd.MM.yyyy'}
                                mask={'__/__/____'}
                                views={['day']}
                            />
                        </Box>
                        <Box sx={styles.endWrapper}>
                            <TextField
                                label={'Stunden'}
                                type={'number'}
                                value={durationHours}
                                inputProps={{ min: 0.25, max: 24, step: 0.25 }}
                                onChange={(e) => setDurationHours(parseFloat(e.target.value) || 0)}
                                sx={{ width: 130 }}
                            />
                        </Box>
                        <Box sx={{ marginLeft: 1 }}>
                            <Typography>{timeDiffDisplay()}</Typography>
                        </Box>
                    </Box>
                )}
                {advancedMode && (
                    <Box sx={styles.startEndWrapper}>
                        {/* eslint-disable max-len */}
                        <Box>
                            <TextField
                                id={'datetime-local'}
                                label={'Start Zeit'}
                                type={'datetime-local'}
                                defaultValue={defaultDateString(DateTime.now()
                                    .minus({ hour: 2 }).startOf('hour').toJSDate())}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{
                                    min: defaultDateString(DateTime.now().minus({ days: MAX_DAY_DIFFERENCE }).toJSDate()),
                                    max: defaultDateString(DateTime.now().toJSDate()),
                                }}
                                onChange={(event: any) => setStart(new Date(event.target.value))}
                            />
                        </Box>
                        <Box sx={styles.endWrapper}>
                            <TextField
                                id={'datetime-local'}
                                label={'End Zeit'}
                                type={'datetime-local'}
                                defaultValue={defaultDateString(DateTime.now().startOf('hour').toJSDate())}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{
                                    min: defaultDateString(DateTime.now().minus({ days: MAX_DAY_DIFFERENCE }).toJSDate()),
                                    max: defaultDateString(DateTime.now().plus({ minutes: MAX_MINUTES_DIFFERENCE }).toJSDate()),
                                }}
                                onChange={(event: any) => setEnd(new Date(event.target.value))}
                            />
                        </Box>
                        {/* eslint-enable max-len */}
                        <Box sx={{ marginLeft: 1 }}>
                            <Typography>{timeDiffDisplay()}</Typography>
                        </Box>
                    </Box>
                )}
                <Box sx={{ marginTop: 2 }}>
                    <TagBar tags={tags} onChange={setTags} availableTags={props.availableTags} />
                </Box>
                <Box sx={{ marginTop: 2 }}>
                    <TextField
                        label={'Beschreibung'}
                        multiline={true}
                        minRows={3}
                        maxRows={3}
                        fullWidth={true}
                        value={description}
                        onChange={event => setDescription(event.target.value)}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose}>
                    Abbrechen
                </Button>
                <Button variant={'contained'} startIcon={<SaveIcon />} onClick={trackTime} disabled={!validTime}>
                    Speichern
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TrackTimeDialog;
